import Button, { LinkButton } from '@bugbug/core/components/Button';
import {
  MenuButton,
  MenuItem,
  MenuPopover,
  MenuProvider,
  MenuSeparator,
} from '@bugbug/core/components/Menu/Menu';
import { Tooltip } from '@bugbug/core/components/Tooltip/RefactoredTooltip';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { SideEffect } from '@bugbug/core/types/utils';
import useAppRoutes from '~/hooks/useAppRoutes';
import useModal from '~/hooks/useModal';
import {
  selectSubscription,
  selectSubscriptionSchedule,
} from '~/modules/organization/organization.selectors';
import { useAppSelector } from '~/modules/store';

import { ChangeParallelModal } from '../ChangeParallelModal/ChangeParallelModal';
import { ConfirmSubscriptionCancelModal } from '../ConfirmSubscriptionCancelModal/ConfirmSubscriptionCancelModal';
import {
  MAX_PARALLEL_RUNS,
  PENDING_DOWNGRADE_INFO,
  SUBSCRIPTION_DETAILS,
  SUBSCRIPTION_PERIOD_DETAILS,
} from '../Subscription/Subscription.constants';
import { getParallelsLabel } from '../Subscription/Subscription.helpers';
import { SectionTitle } from '../Subscription/Subscription.styled';

import * as S from './ActiveSubscriptionCard.styled';

import ActiveSubscriptionIcon from '~/images/active-subscription-icon.svg?react';

interface ActiveSubscriptionCardProps {
  onChangePlan: SideEffect;
}
export const ActiveSubscriptionCard = ({ onChangePlan }: ActiveSubscriptionCardProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'activeSubscription' });
  const { t: tRoot } = useTranslation();
  const activeSubscription = useAppSelector(selectSubscription);
  const subscriptionSchedule = useAppSelector(selectSubscriptionSchedule);
  const parallelAddon = activeSubscription?.addons.find(
    (addon) => addon.type === 'extra_parallel_runs_limit',
  );
  const parallelAddonValue = parallelAddon?.value ?? 0;
  const totalPrice = activeSubscription
    ? activeSubscription.totalPriceWithAddonsAndTax.toFixed(2)
    : '0.00';
  const appRoutes = useAppRoutes('subscription');
  const modal = useModal();

  if (!activeSubscription || activeSubscription.type === 'free' || activeSubscription.isTrial) {
    return null;
  }

  const showParallelModal = () => {
    modal.showCustom(() => (
      <ChangeParallelModal
        initiallySelectedValue={parallelAddonValue + 1}
        currentValue={parallelAddonValue}
      />
    ));
  };

  const switchToAnnual = () => {
    appRoutes.push('checkoutBillingAddress', undefined, {
      type: activeSubscription.type,
      billingCycle: 'yearly',
      parallel: parallelAddonValue.toString(),
    });
  };

  const updateCreditCard = () => {
    appRoutes.push('creditCard');
  };

  const cancelSubscription = () => {
    modal.showCustom(ConfirmSubscriptionCancelModal);
  };

  const details = SUBSCRIPTION_DETAILS[activeSubscription.type];
  const canUpgradeParallelRuns = !parallelAddon || parallelAddon.value < MAX_PARALLEL_RUNS;
  const isDowngradeInProgress = subscriptionSchedule !== null;

  return (
    <S.SectionContainer>
      <S.SubscriptionHeader>
        <SectionTitle>{t('title', 'Active subscription')}</SectionTitle>
        <S.HeaderButtonsContainer>
          {canUpgradeParallelRuns && (
            <Tooltip content={isDowngradeInProgress && PENDING_DOWNGRADE_INFO}>
              <Button
                variant="default"
                iconName="upgrade"
                onClick={showParallelModal}
                disabled={isDowngradeInProgress}
              >
                {t('upgradeParallel', 'Upgrade parallel runs')}
              </Button>
            </Tooltip>
          )}
          <LinkButton
            button={{ variant: 'default', iconName: 'contact' }}
            to={tRoot('default.urls.contact')}
          >
            {t('contactSupport', 'Contact support')}
          </LinkButton>
        </S.HeaderButtonsContainer>
      </S.SubscriptionHeader>
      <S.SubscriptionCard>
        <div>
          <S.PlanName>
            <ActiveSubscriptionIcon />
            <span>{details.name}</span>
          </S.PlanName>
          <S.SubscriptionDetails>
            <S.DetailsHeader>{t('parallelRuns', 'Parallel runs')}</S.DetailsHeader>
            <S.DetailsHeader>{t('billingCycle', 'Billing cycle')}</S.DetailsHeader>
            <S.DetailsHeader>{t('nextPaymentDate', 'Next payment date')}</S.DetailsHeader>
            <S.DetailsHeader>{t('amountDue', 'Amount due')}</S.DetailsHeader>
            <S.DetailsValue>{getParallelsLabel(parallelAddon?.value ?? 0)}</S.DetailsValue>
            <S.DetailsValue>
              {SUBSCRIPTION_PERIOD_DETAILS[activeSubscription.billingCycle].name}
            </S.DetailsValue>
            <S.DetailsValue>
              {isDowngradeInProgress
                ? t('paymentCanceled', '(canceled)')
                : dayjs(activeSubscription.end).format('DD MMM YYYY HH:mm')}
            </S.DetailsValue>
            <S.DetailsValue>
              {isDowngradeInProgress ? t('paymentCanceled', '(canceled)') : `$${totalPrice}`}
            </S.DetailsValue>
          </S.SubscriptionDetails>
        </div>
        <MenuProvider>
          <S.ManageButtonContainer>
            <Tooltip content={isDowngradeInProgress && PENDING_DOWNGRADE_INFO}>
              <MenuButton variant="primary" iconName="settings" disabled={isDowngradeInProgress}>
                {t('manageSubscription', 'Manage subscription')}
                <S.ChevronDownIcon />
              </MenuButton>
            </Tooltip>
          </S.ManageButtonContainer>
          <MenuPopover>
            {canUpgradeParallelRuns && (
              <MenuItem
                iconName="upgradeColored"
                label={t('upgradeParallelRuns.label', 'Upgrade parallel runs')}
                description={t(
                  'upgradeParallelRuns.description',
                  'Increase number of tests you can run at a time',
                )}
                onClick={showParallelModal}
              />
            )}
            {activeSubscription.billingCycle !== 'yearly' && (
              <MenuItem
                iconName="calendar"
                label={t('switchToAnnual.label', 'Switch to annual billing')}
                description={t('switchToAnnual.description', 'Save 15%')}
                onClick={switchToAnnual}
              />
            )}
            <MenuItem
              iconName="editFilled"
              label={t('changePlan.label', 'Change plan')}
              description={t('changePlan.description', 'Upgrade or downgrade')}
              onClick={onChangePlan}
            />
            <MenuItem
              iconName="creditCard"
              label={t('updateCreditCard.label', 'Update credit card')}
              description={t('updateCreditCard.description', 'For example if your card expires')}
              onClick={updateCreditCard}
            />
            <MenuSeparator />
            <MenuItem
              variant="danger"
              iconName="delete"
              label={t('cancelSubscription.label', 'Cancel subscription')}
              description={t('cancelSubscription.description', 'Downgrade to free plan')}
              onClick={cancelSubscription}
            />
          </MenuPopover>
        </MenuProvider>
      </S.SubscriptionCard>
    </S.SectionContainer>
  );
};
